/**
 * Standard timeline view base class for views with identical factory methods.
 *
 * Reduces concrete views to config-only declarations (viewConfig + viewStateName).
 * All four standard views (boards, assignees, dailys, labels) share the same
 * axis configurations, area configurations, data providers, layout, and cursors —
 * only the TimelineViewConfig differs.
 *
 * Note: This introduces a second level of app-specific inheritance
 * (AbstractO2dElement → AbstractO2dTimelineView → AbstractStandardTimelineView → ConcreteView).
 * This is a deliberate exception to the one-level rule (ADR-007/ADR-009) to eliminate
 * ~250 lines of identical code across four views.
 *
 * Fixes:
 * - W-1: Eliminates code duplication across standard timeline views
 * - W-2: Computes cursorX fresh in connectedCallback (not as property initializer)
 */

import { buildDataProviderId } from '@o2d/datasource';
import type { AxisConfiguration, CrosspointDataProviderConfiguration } from '@o2d/datasource';
import type { AreaConfiguration, LayoutTemplate } from '@o2d/grid-layout';
import type { LitElement } from 'lit';
import { html } from 'lit';
import { state } from 'lit/decorators.js';
import { DateTime, Interval } from 'luxon';
import { container } from 'tsyringe';
import { takeUntil } from 'rxjs';
import { AbstractO2dTimelineView } from './abstract-o2d-timeline.view';
import { appState } from '../app.state';
import { AxisDataClient } from '../connectivity/axis-data-client';
import type { PopoverListItem } from '@o2d/popover-list';
import { get } from 'lit-translate';
import { Entity } from '../domain/entities/entity';
import { StringHelper } from '../helpers/string.helper';
import { UiHelper } from '../helpers/ui.helper';
import { EntityManager } from '../persistence/entity.manager';
import { CommandBus } from '../services/command-bus.service';
import { TimelineLoadTracker } from '../services/timeline-load-tracker.service';
import { delayedBusy } from '../services/delayed-busy.helper';
import type { ClassOf } from '../types/class-of.type';
import type { ExtraFixedColumn } from './_shared/types/extra-fixed-column.interface';
import { createTimelineAxisConfigurations } from './_shared/timeline-axis.factory';
import { createTimelineAreaConfigurations } from './_shared/timeline-area.factory';
import { createTimelineDataProviders } from './_shared/timeline-data-providers.factory';
import { TIMELINE_LAYOUT_TEMPLATE, TIMELINE_SECTION_SIZES } from './_shared/timeline-layout';
import type { TimelineViewConfig } from './_shared/timeline-view.config';
import { FilterHelper } from '../helpers/filter.helper';
import { CALENDAR_PROJECT_UID, ISSUE_TYPE_MILESTONE } from '../constants';
import type { FiltersDTO } from '../domain/dtos/filters.dto';
import { Issue } from '../domain/entities/issue.entity';
import { IssueRepository } from '../domain/repositories/issue.repository';
import { belongsToRoot } from '../domain/helpers/issue-type-root.helper';

export abstract class AbstractStandardTimelineView extends AbstractO2dTimelineView {

    /**
     * Entity-specific configuration for this view.
     * Defines entity type, sort order, item path, and includes.
     */
    protected abstract readonly viewConfig: TimelineViewConfig;

    protected readonly contentProviderKey = buildDataProviderId('B', 'D');

    /** Provider key for the fixed calendar row (B × C4). */
    protected readonly calendarProviderKey = buildDataProviderId('B', 'C4');

    /**
     * Returns true if the given key belongs to a content provider that this view handles.
     * Accepts both the main entity provider (B×D) and the calendar provider (B×C4).
     */
    protected override isContentProviderKey(key: string): boolean {
        return key === this.contentProviderKey || key === this.calendarProviderKey;
    }

    /**
     * Opts this view into Y-axis live refresh by returning the configured entity type.
     * The base class uses this to register MessageBroker listeners for
     * entity.created / entity.deleted / entity.changed events (PRD-043).
     */
    protected override getYAxisEntityType(): string | null {
        return this.viewConfig.entityType;
    }

    /** Set of entity UIDs currently highlighted by the user. */
    @state() private _highlightedUids: Set<string> = new Set();

    /** Precompiled filter predicate — returns true if issue matches active filters. */
    private _filterPredicate: (issue: Issue) => boolean = () => true;

    /** Unsubscribe function for the appState.filters subscription. */
    private _filterUnsubscriber?: () => void;

    /**
     * Uid set the activity filter (changedBy/changedWithin) last resolved to — the
     * second parameter FilterHelper.precompileFilters needs to gray out non-matches
     * (plan 7.5). `null` while the filter is inactive, or while a fetch for it has not
     * resolved yet (point 3: there is a short window where nothing is grayed out).
     */
    private _matchedUuids: Set<string> | null = null;

    /**
     * Guards the async uid-set fetch against a stale response overwriting a NEWER
     * selection's result (7.5, point 4): a fast filter switch can make an older fetch
     * resolve after a newer one has already started. Each fetch captures the counter's
     * value at its own start; a response is applied only if that value still matches
     * when it resolves, otherwise it is discarded.
     */
    private _matchedUuidsFetchGeneration = 0;

    /**
     * Date cursor computed fresh in connectedCallback (W-2 fix).
     * Avoids stale values when DOM element is reused across route navigations.
     */
    private _cursorX!: DateTime;

    /**
     * Whether the loading bar shows (ticket AaDIdeKTd4mzxpHJI8SoCA). A plain field, not `@state`:
     * it changes during every drag, and a re-render of the view is not needed to show it.
     */
    private _showLoadingBar = false;

    /**
     * Computes cursorX from AppState BEFORE super.connectedCallback() runs
     * factory methods in Phase 2. Registers click delegation for context menu and highlight button.
     */
    override async connectedCallback(): Promise<void> {
        this._cursorX = DateTime.fromSeconds(appState.dateCursor, { zone: 'utc' });
        // Subscribe before awaiting the base: its synchronous part has already created this
        // connection's disconnected$, and after the await it may already be completed if the view
        // left meanwhile — then the subscription to the app-wide counter would never end.
        const ready = super.connectedCallback();
        delayedBusy(container.resolve(TimelineLoadTracker).busy$)
            .pipe(takeUntil(this.rxjs.disconnected$))
            .subscribe(show => {
                this._showLoadingBar = show;
                this._applyLoadingBar();
            });
        await ready;
        this.addEventListener('click', this._onContextMenuButtonClick);
        this.addEventListener('click', this._onHighlightButtonClick);
        this.addEventListener('click', this._onAddIssueButtonClick);
    }

    /**
     * Applies the loading bar once the timeline is rendered: while the view still shows its own
     * loading text there is no `<o2d-timeline>` to mark yet.
     */
    protected override updated(changedProperties: Map<PropertyKey, unknown>): void {
        super.updated(changedProperties);
        this._applyLoadingBar();
    }

    /** Marks the rendered timeline as loading (class for the bar, aria-busy for assistive tech). */
    private _applyLoadingBar(): void {
        const timeline = this._timeline;
        if (!timeline) {
            return;
        }
        // Runs on every view update as well, so it writes only what changed.
        if (timeline.classList.contains('is-loading') === this._showLoadingBar) {
            return;
        }
        timeline.classList.toggle('is-loading', this._showLoadingBar);
        if (this._showLoadingBar) {
            timeline.setAttribute('aria-busy', 'true');
        } else {
            timeline.removeAttribute('aria-busy');
        }
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._filterUnsubscriber?.();
        this.removeEventListener('click', this._onContextMenuButtonClick);
        this.removeEventListener('click', this._onHighlightButtonClick);
        this.removeEventListener('click', this._onAddIssueButtonClick);
    }

    /**
     * Refreshes `_cursorX` from the live `appState.dateCursor` (O2D-PRD-096 Seam B).
     *
     * `createAxisConfigurations()` bakes the X origin from `_cursorX`, which is
     * otherwise set only in `connectedCallback` (:97). Without this refresh, a
     * re-init after a Y-axis delete would rebuild the time axis around the stale
     * connect-time date. Uses the same conversion as `connectedCallback`:
     * `DateTime.fromSeconds(appState.dateCursor, { zone: 'utc' })`.
     *
     * @override
     * @protected
     */
    protected override _refreshCursorXFromAppState(): void {
        this._cursorX = DateTime.fromSeconds(appState.dateCursor, { zone: 'utc' });
    }

    protected createAxisConfigurations(): AxisConfiguration<string>[] {
        const config: TimelineViewConfig = {
            ...this.viewConfig,
            onFirstFetch: (firstShortId: string) => {
                appState.setViewCursorY(this.viewStateName, firstShortId);
            }
        };
        // `this.createLayoutTemplate()`, NEVER `this._layoutTemplate`: connectedCallback
        // assigns that field AFTER this method has already run (abstract-o2d-timeline.view
        // :721 vs :724), so a field read would be `undefined` on first render of every
        // view — and since the build only transpiles, TypeScript would not stop it.
        return createTimelineAxisConfigurations(this._cursorX, config, this.createLayoutTemplate());
    }

    /**
     * Jumps the time (X) axis to today. The X origin is baked into the axis
     * configurations from `_cursorX` (timeline-axis.factory), so a live scrollToValue
     * cannot reach a far date — the datetime axis has no `expandToValue`. Instead we
     * re-create the axis configs around today and swap them into the timeline via
     * `setAxisConfigurationsAndWait`, which rebuilds the axes at today — the same
     * origin path the initial load uses. Also persists appState.dateCursor so the
     * position survives reloads.
     *
     * Today is indented `COLUMN_OFFSET` columns from the left edge (the X origin is a
     * few days before today) so recent past stays visible — mirroring the Y anchor that
     * places the nearest-milestone project at ~row 3.
     *
     * @override
     */
    protected override async onFocusToday(): Promise<void> {
        // Columns of past shown left of today (mirrors the Y anchor's ~row-3 offset).
        const COLUMN_OFFSET = 2;
        this._cursorX = DateTime.now().minus({ days: COLUMN_OFFSET });
        appState.dateCursorDate = this._cursorX;
        this._axisConfigurations = this.createAxisConfigurations();

        await this._timeline?.setAxisConfigurationsAndWait(this._axisConfigurations);
    }

    /**
     * Additional fixed row-header columns this view wants, beyond title / add-issue /
     * context menu. Base: none, so nothing changes for the six views that do not override
     * it. The board view returns its "add milestone" column here.
     *
     * A method rather than a field so the value is on the prototype: the view test drives
     * prototype methods against a stand-in receiver, which would not carry an instance
     * field.
     */
    protected getExtraFixedColumns(): ExtraFixedColumn[] {
        return [];
    }

    /**
     * Single place that builds the area list.
     *
     * Extracted because the factory used to be called from TWO places — here and again,
     * raw, inside the filter subscription below. Supplying the extra columns to only one of
     * them produced no error on load; it crashed on the FIRST FILTER CHANGE, when the axis
     * expected four column sections and the freshly rebuilt area list carried three. One
     * builder makes that class of mistake impossible instead of merely unlikely.
     */
    private buildAreaConfigurations(): AreaConfiguration[] {
        return createTimelineAreaConfigurations(
            (uid) => this._highlightedUids.has(uid),
            (issue) => !belongsToRoot(issue.type, ISSUE_TYPE_MILESTONE) && !this._filterPredicate(issue),
            this.getExtraFixedColumns(),
            (uid, axisTitle) => this.renderRowTitle(uid, axisTitle)
        );
    }

    /** What a row's title cell shows; the axis title by default. /by-type renders its own. */
    protected renderRowTitle(_entityUid: string, axisTitle: string): unknown {
        return axisTitle;
    }

    protected createAreaConfigurations(): AreaConfiguration[] {
        // Lazy-init filter subscription (idempotent — skips if already subscribed)
        if (!this._filterUnsubscriber) {
            if (appState.hasAppliedFilters()) {
                this._applyActivityFilterAndPredicate(appState.filters);
            }
            this._filterUnsubscriber = appState.subscribe(() => {
                if (appState.hasAppliedFilters()) {
                    this._applyActivityFilterAndPredicate(appState.filters);
                } else {
                    this._filterPredicate = () => true;
                    this._matchedUuids = null;
                }
                // Rebuild area configurations with updated predicate closure
                this._areaConfigurations = this.buildAreaConfigurations();
            }, 'filters');
        }

        return this.buildAreaConfigurations();
    }

    /**
     * Recomputes _filterPredicate synchronously and, if the activity filter
     * (changedWithin) is set, kicks off the async uid-set fetch (7.5). _matchedUuids is
     * reset to null FIRST when the activity filter is active — a stale set from a
     * DIFFERENT prior selection would be a worse error than the accepted "nothing
     * grayed out yet" window, and precompileFilters must see the reset value, not the
     * old one, when building this predicate.
     */
    private _applyActivityFilterAndPredicate(filters: FiltersDTO): void {
        this._matchedUuids = null;
        if (FilterHelper.isActivityFilterActive(filters)) {
            this._fetchMatchedUuids(filters);
        }
        this._filterPredicate = FilterHelper.precompileFilters(filters, this._matchedUuids);
    }

    /**
     * Fires the timeline's activity-filter uid-set fetch and, if it resolves without
     * being superseded, stores the result and rebuilds the area configurations a SECOND
     * time (7.5, points 2/4). Reuses the tasklist endpoint (findAllUuidsByStatus) with
     * listMode 'all' — the default 'tasks' mode would exclude absences/holidays/past
     * timeframes from the uid set, which the (negated) predicate would then treat as
     * permanent non-matches (7.5's own correction on this point).
     *
     * The two filter keys are NOT built into QueryParams by hand — they go through
     * FilterHelper.asQueryParams like every other filter (7.1: exactly one place
     * translates them), carrying only these two keys so the request the timeline sends
     * cannot drift from what S5's translation test already covers.
     */
    private _fetchMatchedUuids(filters: FiltersDTO): void {
        const generation = ++this._matchedUuidsFetchGeneration;
        container.resolve(IssueRepository)
            .findAllUuidsByStatus(FilterHelper.asQueryParams(FilterHelper.activityFilterOnly(filters)), null, 'all')
            .then(items => {
                if (generation !== this._matchedUuidsFetchGeneration) {
                    return; // superseded by a newer selection's fetch — discard
                }
                this._matchedUuids = new Set(items.map(item => item.uid));
                this._filterPredicate = FilterHelper.precompileFilters(filters, this._matchedUuids);
                this._areaConfigurations = this.buildAreaConfigurations();
            });
    }

    protected createDataProviderConfigurations(): CrosspointDataProviderConfiguration<any, any, any>[] {
        // Template via the method, not the `_layoutTemplate` field — same reason as in
        // createAxisConfigurations() above: the field is still unset at this point.
        return createTimelineDataProviders(this.viewConfig, this.createLayoutTemplate());
    }

    protected createLayoutTemplate(): LayoutTemplate {
        return TIMELINE_LAYOUT_TEMPLATE;
    }

    protected async createStartCursors(): Promise<{ axisId: string; value: string | number }[]> {
        // Y-axis needs a non-empty start cursor (entityAxisBuilder throws on falsy origin).
        // Use persisted cursor if available, otherwise fetch first entity from backend.
        let yStartCursor = this.getYAxisStartCursor();
        if (!yStartCursor) {
            yStartCursor = await this.fetchFirstEntityUid();
        }

        return [
            { axisId: 'A', value: 0 },
            { axisId: 'B', value: this._cursorX.toISODate()! },
            { axisId: 'C0', value: 0 },
            { axisId: 'C1', value: 0 },
            { axisId: 'C2', value: 0 },
            { axisId: 'C3', value: 0 },
            { axisId: 'C4', value: CALENDAR_PROJECT_UID },
            { axisId: 'D', value: yStartCursor },
        ];
    }

    /**
     * Fetches the first entity UID from backend for initial Y-axis positioning.
     * Called only on first visit when no cursor is persisted in ViewState.
     */
    protected async fetchFirstEntityUid(): Promise<string> {
        const axisDataClient = container.resolve(AxisDataClient);
        const response = await axisDataClient.fetchAxisData({
            entityType: this.viewConfig.entityType,
            referenceUid: null,
            offset: 0,
            limit: 1,
            sort: this.viewConfig.sort,
            ...(this.viewConfig.queryParams?.length ? { queryParams: this.viewConfig.queryParams } : {})
        });

        if (response.values.length > 0) {
            const firstShortId = response.values[0][1];
            // Persist for subsequent visits
            appState.setViewCursorY(this.viewStateName, firstShortId);
            return firstShortId;
        }

        // No entities exist — return empty string (axis will remain empty)
        return '';
    }

    protected getSectionSizeVariables(): Record<string, string> {
        return TIMELINE_SECTION_SIZES;
    }

    // --- Highlight Button ---

    /**
     * Click delegation handler for the row-header title (highlight toggle).
     * Toggles the entity UID in _highlightedUids (immutable Set swap)
     * and triggers re-render on grid-area-0 (title) and grid-area-7 (underlay).
     */
    private _onHighlightButtonClick = (evt: Event): void => {
        const target = (evt.target as HTMLElement).closest('[data-role="highlight"]') as HTMLElement | null;
        if (!target) return;

        const uid = target.dataset.entityUid;
        if (!uid) return;

        const updated = new Set(this._highlightedUids);
        if (updated.has(uid)) {
            updated.delete(uid);
        } else {
            updated.add(uid);
        }
        this._highlightedUids = updated;

        const timeline = this.querySelector('o2d-timeline');
        if (timeline) {
            const area0 = timeline.querySelector('#grid-area-0') as LitElement | null;
            const area7 = timeline.querySelector('#grid-area-7') as LitElement | null;
            area0?.requestUpdate();
            area7?.requestUpdate();
        }
    };

    // --- Add-Issue Button ---

    /**
     * Click delegation handler for the row-header "+" (add issue) button.
     * Builds an Issue template pre-filled via the view's populateTemplateFromYAxis
     * hook (already correctly overridden per concrete view — Project for Boards/
     * Dailys, Keyword for Labels, IssueStatus for IssueStatus, Employee default for
     * Assignees), due today, and opens the create dialog on that template.
     *
     * Single-entity dialog (UiHelper.openDialogEntityEdit), not
     * IssueHelper.openIssuesEditDialog — this button never has an existing-issues
     * list to merge into, unlike handleContentCellClicked's Ctrl+click, so the
     * multi-issue machinery isn't needed. The dialog itself still internally clones
     * `template` once (AbstractEntitiesDialogComponent.createNewEntity(), which every
     * creationMode dialog runs automatically on open) — that used to silently drop
     * the assignment populateTemplateFromYAxis just set, since Issue.clone() didn't
     * copy employeeToIssueAssignments; fixed at the source in issue.entity.ts rather
     * than worked around here, since the same clone() gap also broke the dialog's own
     * "Klonen" button for every issue with an assignee.
     *
     * No date/cell to derive a due date from here, so it defaults to today (start of
     * day, one-day interval) — same interval shape handleContentCellClicked uses for
     * its clicked date.
     */
    private _onAddIssueButtonClick = async (evt: Event): Promise<void> => {
        const target = (evt.target as HTMLElement).closest('[data-role="add-issue"]') as HTMLElement | null;
        if (!target) return;

        const uid = target.dataset.entityUid;
        if (!uid) return;

        const template = await container.resolve(IssueRepository).create();
        const today = DateTime.utc().startOf('day');
        template.timeRange = Interval.fromDateTimes(today, today.plus({ days: 1 }));
        await this.populateTemplateFromYAxis(template, uid);

        UiHelper.openDialogEntityEdit(Issue, template, { creationMode: true, closable: true });
    };

    // --- Context Menu ---

    /**
     * Builds context menu items for a Y-axis entity row.
     * Override in subclasses to add entity-specific guards (e.g., isFixed, referencesCount).
     */
    protected buildRowContextMenu(_entity: Entity): PopoverListItem[] | Promise<PopoverListItem[]> {
        return [
            { id: 'edit', label: get('ha-boards-view.row-title.edit'), type: 'item' },
            { id: 'delete', label: get('ha-boards-view.row-title.delete'), type: 'item' },
        ];
    }

    /** Opens the entity edit dialog for the given entity. */
    protected handleContextMenuEdit(entity: Entity): void {
        UiHelper.openDialogEntityEdit(
            entity.constructor as ClassOf<Entity>,
            entity,
            { closable: true, minimizable: false, maximizable: false, title: '' }
        );
    }

    /** Shows a confirm dialog and deletes the entity if confirmed. */
    protected async handleContextMenuDelete(entity: Entity): Promise<void> {
        const confirmed = await UiHelper.confirm({
            title: 'In den Papierkorb verschieben',
            content: html`<p>Wollen Sie wirklich &laquo;${entity.toString()}&raquo; in den Papierkorb verschieben?</p>`,
            confirmLabel: 'Ja, jetzt löschen!',
            cancelLabel: 'Nein, abbrechen!'
        });
        if (!confirmed) return;

        // Seam A: if the deleted row is the persisted Y-cursor, advance the cursor to a
        // surviving neighbor BEFORE dispatch. Seam B's immutable re-init re-origins the
        // Y-axis on getViewCursorY (createStartCursors); leaving it on the just-deleted uid
        // would 400 on axis-expand. Fallback chain: offset+1 neighbor → offset-1 neighbor →
        // first-entity ('' if none). Mirrors fetchFirstEntityUid's AxisDataClient shape.
        if (entity.uid === appState.getViewCursorY(this.viewStateName)) {
            const neighborAtOffset = async (offset: number): Promise<string> => {
                const axisDataClient = container.resolve(AxisDataClient);
                const response = await axisDataClient.fetchAxisData({
                    entityType: this.viewConfig.entityType,
                    referenceUid: entity.uid,
                    offset,
                    limit: 1,
                    sort: this.viewConfig.sort,
                    ...(this.viewConfig.queryParams?.length ? { queryParams: this.viewConfig.queryParams } : {})
                });
                return response.values.length > 0 ? response.values[0][1] : '';
            };
            const newCursor = (await neighborAtOffset(1)) || (await neighborAtOffset(-1)) || (await this.fetchFirstEntityUid());
            appState.setViewCursorY(this.viewStateName, newCursor);
        }

        const commandBus = container.resolve(CommandBus);
        const commandPrototype = commandBus.getCommand(entity.constructor as ClassOf<Entity>, 'delete');
        if (commandPrototype) {
            await commandBus.dispatchCommand(new commandPrototype(entity));
        }
    }

    /**
     * Click delegation handler for context menu buttons.
     * Finds entity by data-entity-uid, builds menu via hook, handles selection.
     */
    private _onContextMenuButtonClick = async (evt: Event): Promise<void> => {
        const target = (evt.target as HTMLElement).closest('[data-role="context-menu"]') as HTMLElement | null;
        if (!target) return;

        const entityUid = target.dataset.entityUid;
        if (!entityUid) return;

        const entityManager = container.resolve(EntityManager);
        const kebabName = StringHelper.toKebabCase(this.viewConfig.entityType);
        const repository = entityManager.getRepositoryForEntityByName<Entity>(kebabName);
        if (!repository) return;

        const entity = await repository.findOneById(entityUid) as Entity | null;
        if (!entity) return;

        const menuItems = await this.buildRowContextMenu(entity);
        const selected = await UiHelper.showContextMenu(target, menuItems);

        if (selected?.id === 'edit') {
            this.handleContextMenuEdit(entity);
        } else if (selected?.id === 'delete') {
            await this.handleContextMenuDelete(entity);
        } else if (selected?.id === 'open-link') {
            UiHelper.openExternalLink(selected.payload as string | undefined);
        }
    };
}
